import { Modal, Text, Tooltip } from "@mantine/core";
import {
  IconCertificate,
  IconHelp,
  IconHome,
  IconLogout,
  IconMovie,
  IconRefresh,
  IconRefreshOff,
  IconSearch,
  IconSearchOff,
  IconSettings,
} from "@tabler/icons-react";
import { useCallback, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useGetApi,
  useElectronApiReady,
  useIsOnline,
  useLogout,
} from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setYachtdata } from "src/redux/slices/entriesGeneratorSlice";
import {
  setAreChecklistsDisabled,
  setPermsProperty,
  setUserType,
} from "src/redux/slices/permsSlice";
import {
  setPathNames,
  setPreferences,
  setTheme,
} from "src/redux/slices/preferencesSlice";
import { getYachtId } from "src/utils";

import useOnPageNavigation from "../../../hooks/useOnPageNavigation";
import { useClearCursorPositions } from "../../../hooks/useSavedCursorPosition";
import { useClearScrollState } from "../../../hooks/useSavedHorizontalScroll";
import { setNoSaves } from "../../../redux/slices/entrySlice";
import {
  clearPopups,
  setNMEAModalData,
  // setWasNMEAModalClosed,
  // setWasSaved,
} from "../../../redux/slices/generatorFeaturesSlice";
import {
  FlexDiv,
  HeaderContainer,
  HeaderIcon,
  JustifyEnd,
  LogoContainer,
  Sea,
  Sky,
} from "./Header.styled";
import { useTheme } from "../../../hooks/useTheme";
import LogoutTimer from "./LogoutTimer/LogoutTimer";
import PendingRequestsInfo from "./PendingRequestsInfo/PendingRequestsInfo";
import SyncingLoader from "./SyncingLoader";
import { Flex } from "../../../styled";
import { Button } from "../Button/Button";
import { IPathData } from "../../../interfaces/PathData";

interface IUserData {
  yachtname: string;
  rolename: string;
  full_name: string;
}

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isOnline = useIsOnline();
  const electronApiReady = useElectronApiReady();
  const { pathname } = useLocation();
  const logout = useLogout();
  const clearScrollState = useClearScrollState();
  const clearCursorPositions = useClearCursorPositions();
  const { yachtuser, management, clientAdmin, reviewer, auditor } = useSelector(
    state => state.permsSlice,
  );
  const { theme, origLogo, versionMessage } = useSelector(
    state => state.preferencesSlice,
  );
  const [expiredModal, setExpiredModal] = useState<string | null>(null);
  const customTheme = useTheme();

  useOnPageNavigation((currentPath, previusPath) => {
    clearScrollState();
    clearCursorPositions();
    dispatch(setNoSaves(0));
    dispatch(clearPopups());
    if (!currentPath.includes("new")) dispatch(setNMEAModalData(null));
    // dispatch(setWasSaved(false));
    // dispatch(setWasNMEAModalClosed(false));
    if (currentPath.includes("logbook")) {
      localStorage.setItem("isInLogbook", "true");
    } else {
      localStorage.setItem("isInLogbook", "false");
    }
  });

  const mapMyData = useCallback(
    (data: any) => {
      if (Object.keys(data.data).includes("checklists"))
        dispatch(setAreChecklistsDisabled(!data.data.checklists));

      if (data.data.data?.settings?.logoutTime) {
        dispatch(
          setPreferences({
            logoutTime: data.data.data.settings.logoutTime,
          }),
        );
      }

      if (data.data.data) {
        dispatch(
          setPreferences({
            theme: data.data.data.settings?.theme,
            uppercase: data.data.yachtdata?.uppercase,
          }),
        );

        dispatch(setYachtdata(data.data.yachtdata));
      }
      if (data?.data?.userclient)
        dispatch(
          setPermsProperty({ key: "clientid", value: data.data.userclient }),
        );
      if (data.data.medRoles && Array.isArray(data.data.medRoles)) {
        const medRoles = data.data.medRoles.filter((role: string) =>
          role.includes(data.data.rolename ?? ""),
        );
        !!data.data.medofficer ? medRoles.push("Med Officer") : null;
        dispatch(setPermsProperty({ key: "medroles", value: medRoles }));
      }
      if (data.data.rolename)
        dispatch(
          setPermsProperty({ key: "rolename", value: data.data.rolename }),
        );
      if (data.data.NonMaritime !== undefined) {
        dispatch(
          setPermsProperty({
            key: "nonMaritime",
            value: data.data.NonMaritime === 1,
          }),
        );
      }
      if (data.data.data.perms)
        dispatch(
          setPermsProperty({ key: "perms", value: data.data.data.perms }),
        );
      if (data.data.full_name)
        dispatch(
          setPermsProperty({ key: "fullName", value: data.data.full_name }),
        );

      if (data.data.usertype) dispatch(setUserType(data.data.usertype));

      if (data.data && "genAccountDaysLeft" in data.data) {
        dispatch(setPermsProperty({ key: "isGenericAccount", value: true }));
        if (
          data.data.genAccountDaysLeft === 0 &&
          data.data.genAccountExpiring
        ) {
          setExpiredModal(data.data.genAccountExpiringMessage);
        }
      }

      if (data?.data?.yachtdata?.url) {
        dispatch(
          setTheme({
            theme: "Custom",
            customThemeUrl: data.data.yachtdata.url,
            origLogo: data.data.clientdata.origLogo,
          }),
        );
      } else if (data?.data?.clientdata?.theme) {
        const theme = data.data.clientdata.theme
          .split("/")
          .filter((e: string) => !!e)
          .reverse()[0];
        dispatch(setTheme({ theme, origLogo: data.data.clientdata.origLogo }));
      } else {
        dispatch(setTheme({ theme: null, origLogo: true }));
      }

      return data.data;
    },
    [dispatch],
  );

  const mapPathsData = useCallback(
    (data: { data: { content: IPathData[] } }) => {
      if (!data?.data?.content) return;
      const pathNames: Record<string, string> = {};

      const pathsMapper = (path: IPathData) => {
        pathNames[path?.sect?.replaceAll("_", "").toLowerCase()] = path.name;
        if (path.subSects) Object.values(path.subSects).forEach(pathsMapper);
      };
      data.data.content.forEach(pathsMapper);
      dispatch(setPathNames(pathNames));
    },
    [dispatch],
  );

  const { data } = useGetApi<IUserData>({
    request: "get-me",
    mapper: mapMyData,
    body: {
      params: {
        yachtid: getYachtId(),
      },
    },
    fetchOnInit: !pathname.includes("/exports"),
  });

  const { pathNames } = useSelector(state => state.preferencesSlice);

  useGetApi({
    request: "get-settings",
    mapper: mapPathsData,
    fetchOnInit:
      !pathname.includes("/exports") && Object.keys(pathNames).length === 0,
  });

  const handleCloseExpiredModal = () => {
    void logout();
    setExpiredModal(null);
  };
  return (
    <>
      <HeaderContainer>
        {!customTheme?.hideHeader && (
          <Sky>
            <FlexDiv>
              {yachtuser && (
                <>
                  {(management || clientAdmin) && (
                    <Tooltip label="Management" openDelay={300}>
                      <HeaderIcon
                        data-cy="management-header-icon"
                        size={"lg"}
                        color="headerColor"
                        onClick={() => navigate("/management")}>
                        <IconCertificate />
                      </HeaderIcon>
                    </Tooltip>
                  )}
                  <Tooltip label="Home" openDelay={300}>
                    <HeaderIcon
                      data-cy="home-header-icon"
                      size={"lg"}
                      color="headerColor"
                      onClick={() => navigate("/home")}>
                      <IconHome />
                    </HeaderIcon>
                  </Tooltip>
                  <Tooltip label="Help Videos" openDelay={300}>
                    <HeaderIcon
                      data-cy="help-videos-header-icon"
                      size={"lg"}
                      color="headerColor"
                      onClick={() => {
                        navigate("/help-videos");
                      }}>
                      <IconMovie strokeWidth="1.75" />
                    </HeaderIcon>
                  </Tooltip>
                </>
              )}

              {(yachtuser || reviewer || auditor) && (
                <Tooltip label="FAQ" openDelay={300}>
                  <a
                    href="https://www.flipbookpdf.net/web/site/9f532a146b6ef05feddfd73bd940b3707df40992FBP30084359.pdf.html"
                    target="_blank"
                    rel="noreferrer">
                    <HeaderIcon
                      data-cy="faq-header-icon"
                      size={"lg"}
                      color="headerColor">
                      <IconHelp strokeWidth="1.75" />
                    </HeaderIcon>
                  </a>
                </Tooltip>
              )}
            </FlexDiv>
            <JustifyEnd>
              {electronApiReady && <SyncingLoader />}
              {electronApiReady && window.api?.sync && (
                <Tooltip label="Sync" openDelay={300}>
                  <HeaderIcon
                    data-cy="sync-header-icon"
                    size={"lg"}
                    color="headerColor"
                    onClick={() => isOnline && window.api?.sync()}>
                    {isOnline ? <IconRefresh /> : <IconRefreshOff />}
                  </HeaderIcon>
                </Tooltip>
              )}
              {!electronApiReady && data && <LogoutTimer />}
              {yachtuser && (
                <>
                  <Tooltip label="Search" openDelay={300}>
                    <HeaderIcon
                      data-cy="search-header-icon"
                      size={"lg"}
                      color="headerColor"
                      onClick={() => isOnline && navigate("/search")}>
                      {isOnline ? <IconSearch /> : <IconSearchOff />}
                    </HeaderIcon>
                  </Tooltip>

                  <Tooltip label="Settings" openDelay={300}>
                    <HeaderIcon
                      data-cy="settings-header-icon"
                      size={"lg"}
                      color="headerColor"
                      onClick={() => {
                        navigate("/settings");
                      }}>
                      <IconSettings />
                    </HeaderIcon>
                  </Tooltip>
                </>
              )}

              <Tooltip label="Log Out" openDelay={500}>
                <HeaderIcon
                  data-cy="logout-header-icon"
                  size={"lg"}
                  className="header-icon"
                  color="headerColor"
                  onClick={logout}>
                  <IconLogout />
                </HeaderIcon>
              </Tooltip>
            </JustifyEnd>
          </Sky>
        )}
        {!customTheme?.hideHeader && (
          <Sea>
            <Flex align="center" w="100%" justify="space-between">
              <Flex>
                <Text fw={600} c="headerColor">
                  {data?.yachtname ? data.yachtname : null}
                </Text>
                <Text fw={500} c="headerColor" ml={30}>
                  {`${data?.rolename ?? ""} ${
                    data?.rolename && data?.full_name ? "/" : ""
                  } ${data?.full_name ?? ""}`}
                </Text>
              </Flex>
              <Flex
                gap={20}
                align="center"
                justify="flex-end"
                style={{ flexBasis: "50%" }}>
                <PendingRequestsInfo />
                {versionMessage && (
                  <Flex h="100%" style={{ flexBasis: "40%" }}>
                    <Text
                      c={versionMessage.warning ? "red" : "green"}
                      size="xs">
                      {versionMessage.message}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Sea>
        )}
        <LogoContainer>
          {origLogo && <img src="assets/eLogLogoV2.png" />}
          {theme === "DSNM" && (
            <img src="assets/DSNM White and Gold Logo.png" />
          )}
        </LogoContainer>
      </HeaderContainer>
      <Modal
        opened={expiredModal !== null}
        onClose={handleCloseExpiredModal}
        title="Warning">
        <Text ta="center">{expiredModal}</Text>
        <Flex justify="center" w="100%" mt={10}>
          <Button onClick={handleCloseExpiredModal}>Logout</Button>
        </Flex>
      </Modal>
    </>
  );
};

export default Header;
